import strings from 'localization/strings';
import { formatPercentage, formatAmount, formatNumber } from 'helpers/StringHelper';
import { getValueForCurrency, getCurrency, getDataPoint } from 'logic/capacityUtilizationLogic';
import capacityUtilizationCategories from 'enums/capacityUtilizationCategories';

const formatPercentageCreator = (appContext, { round } = { round: true }) => value => formatPercentage(value ?? 0, appContext, { maxNumberOfDecimals: round ? 1 : undefined });
const formatAmountCreator = (appContext, { currency } = {}) => value => formatAmount(value, currency, appContext);
const formatAmountFromObjectCreator = amountSumSelector => (capacityUtilization, appContext) => {
    const amountSum = amountSumSelector(capacityUtilization);
    const currency = getCurrency(amountSum);
    return formatAmount(getValueForCurrency(amountSum, currency), currency, appContext);
};
// formatValueCreator: (appContext, { round: bool, currency: string }) => value: number => string
// formatValueFromObject: (capacityUtilization: { categoryDataPoints, hasTotal, interval }, appContext, { round: bool }) => string
// getNumericValue: capacityUtilization: { categoryDataPoints, hasTotal, interval } => number

const capacityUtilizationProperties = {
    bookedCount: {
        getNumericValue: capacityUtilization => getDataPoint(capacityUtilization, capacityUtilizationCategories.booked).count
    },
    bookedArea: {
        getNumericValue: capacityUtilization => getDataPoint(capacityUtilization, capacityUtilizationCategories.booked).area
    },
    totalCount: {
        getNumericValue: capacityUtilization => getDataPoint(capacityUtilization, capacityUtilizationCategories.total).count
    },
    totalArea: {
        getNumericValue: capacityUtilization => getDataPoint(capacityUtilization, capacityUtilizationCategories.total).area
    },
    countCapacityUtilization: {
        getNumericValue: capacityUtilization => safeDiv(getDataPoint(capacityUtilization, capacityUtilizationCategories.booked).count, getDataPoint(capacityUtilization, capacityUtilizationCategories.total).count),
        formatValueCreator: formatPercentageCreator,
        additionalColumns: ['bookedCount', 'totalCount']
    },
    areaCapacityUtilization: {
        getNumericValue: capacityUtilization => safeDiv(getDataPoint(capacityUtilization, capacityUtilizationCategories.booked).area, getDataPoint(capacityUtilization, capacityUtilizationCategories.total).area),
        formatValueCreator: formatPercentageCreator,
        additionalColumns: ['bookedArea', 'totalArea']
    },
    amount: {
        getNumericValue: capacityUtilization => getDataPoint(capacityUtilization, capacityUtilizationCategories.booked).value.currencySums[0]?.value,
        formatValueCreator: formatAmountCreator,
        formatValueFromObject: formatAmountFromObjectCreator(capacityUtilization => getDataPoint(capacityUtilization, capacityUtilizationCategories.booked).value)
    },
    totalValue: {
        getNumericValue: capacityUtilization => getDataPoint(capacityUtilization, capacityUtilizationCategories.total).value.currencySums[0]?.value,
        formatValueCreator: formatAmountCreator,
        formatValueFromObject: formatAmountFromObjectCreator(capacityUtilization => getDataPoint(capacityUtilization, capacityUtilizationCategories.total).value)
    },
    businessBookingCountPercentage: {
        getNumericValue: capacityUtilization => safeDiv(getDataPoint(capacityUtilization, capacityUtilizationCategories.businessBooked).count, getDataPoint(capacityUtilization, capacityUtilizationCategories.booked).count),
        formatValueCreator: formatPercentageCreator
    },
    businessBookingAreaPercentage: {
        getNumericValue: capacityUtilization => safeDiv(getDataPoint(capacityUtilization, capacityUtilizationCategories.businessBooked).area, getDataPoint(capacityUtilization, capacityUtilizationCategories.booked).area),
        formatValueCreator: formatPercentageCreator
    }
};

Object.keys(capacityUtilizationProperties).forEach(key => {
    capacityUtilizationProperties[key].key = key;
    capacityUtilizationProperties[key].yAxis = capacityUtilizationProperties[key].yAxis ?? {};
    capacityUtilizationProperties[key].formatValueCreator = capacityUtilizationProperties[key].formatValueCreator ?? (appContext => value => formatNumber(value ?? 0, appContext));
    capacityUtilizationProperties[key].formatValueFromObject = capacityUtilizationProperties[key].formatValueFromObject
      ?? ((capacityUtilization, appContext, settings) => capacityUtilizationProperties[key].formatValueCreator(appContext, settings)(capacityUtilizationProperties[key].getNumericValue(capacityUtilization)));
});

strings.addLoadCallback(() => {
    Object.values(capacityUtilizationProperties).forEach(o => {
        o.title = strings.capacityUtilizationProperties[o.key];
    });
});

const safeDiv = (n, d) => d
    ? n / d
    : 0;

export default capacityUtilizationProperties;
